import React, { Key } from "react";

import { Button } from "shared/components/Button";

import {
  FallbackBody,
  FallbackCard,
  FallbackState,
  FallbackTitle,
} from "./Vero1ErrorBoundary.styled";
import {
  emitVero1BoundaryError,
  Vero1BoundaryContext,
} from "./Vero1ReviewPanel/vero1Telemetry";

interface Vero1ErrorBoundaryProps {
  context: Vero1BoundaryContext;
  dealId?: Key;
  checklistId?: Key;
  framed?: boolean;
  onErrorStateChange?: (errored: boolean) => void;
  children: React.ReactNode;
}

interface Vero1ErrorBoundaryState {
  resetKey: number;
  hasError: boolean;
  errorIdentity: string | null;
}

const identityOf = (dealId?: Key, checklistId?: Key) =>
  `${dealId ?? ""}:${checklistId ?? ""}`;

export default class Vero1ErrorBoundary extends React.Component<
  Vero1ErrorBoundaryProps,
  Vero1ErrorBoundaryState
> {
  constructor(props: Vero1ErrorBoundaryProps) {
    super(props);
    this.state = { resetKey: 0, hasError: false, errorIdentity: null };
  }

  static getDerivedStateFromError(): Partial<Vero1ErrorBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: Vero1ErrorBoundaryProps,
    state: Vero1ErrorBoundaryState
  ): Partial<Vero1ErrorBoundaryState> | null {
    if (state.hasError) {
      const current = identityOf(props.dealId, props.checklistId);
      if (state.errorIdentity !== null && state.errorIdentity !== current) {
        return { hasError: false, errorIdentity: null };
      }
    }
    return null;
  }

  componentDidUpdate(_prev: Vero1ErrorBoundaryProps, prevState: Vero1ErrorBoundaryState) {
    if (
      prevState.hasError &&
      !this.state.hasError &&
      this.state.resetKey === prevState.resetKey
    ) {
      this.props.onErrorStateChange?.(false);
    }
  }

  componentDidCatch(error: unknown) {
    const { context, dealId, checklistId, onErrorStateChange } = this.props;
    emitVero1BoundaryError(error, { context, dealId, checklistId });
    this.setState({ errorIdentity: identityOf(dealId, checklistId) });
    onErrorStateChange?.(true);
  }

  private retry = () => {
    const { onErrorStateChange } = this.props;
    onErrorStateChange?.(false);
    this.setState((prev) => ({
      hasError: false,
      errorIdentity: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  render() {
    const { children } = this.props;
    const { hasError, resetKey } = this.state;

    if (hasError) {
      const { framed } = this.props;
      const fallback = (
        <FallbackState data-testid="vero1-error-boundary-fallback">
          <FallbackTitle>This review couldn&apos;t load</FallbackTitle>
          <FallbackBody>
            Something went wrong showing this review. Nothing you submitted is
            lost — try again.
          </FallbackBody>
          <Button type="primary" onClick={this.retry}>
            Try again
          </Button>
        </FallbackState>
      );
      return framed ? <FallbackCard>{fallback}</FallbackCard> : fallback;
    }

    return <React.Fragment key={resetKey}>{children}</React.Fragment>;
  }
}
